import { useContext, useState, useEffect, useCallback } from "react";
import { ApplicationsContext } from "../applications-context";
import { Typography, Dialog, DialogContent, DialogTitle } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit'
import { useTranslation } from "react-i18next";
import { StyledButton } from "../../../components/styled-button/styled-button";
import { useService } from "../applications-service";
import pubSub, { EVENTS } from "../../../config/pubSub";
import { IAddEditProviderModel } from "../application-upsert-form";
import EditCustomerComponent from "./edit-component";
import { styled } from '@mui/material/styles';
import { AppConfig } from "../../../context/context";
import { AppContainer, StyledDialogContent, StyledDialogTitle, StyledTypography } from "../../../components/shared-styles/dialog-title-styles";

export const EditApplicationComponent = () => {
  const [openEditApplication, setOpenEditApplication] = useState<boolean>(false);
  const { isAppListLoading, selectedApp, setSelectedApp, setSelectedCheckedApps } = useContext(ApplicationsContext);
 
  const { t } = useTranslation();

  const handleEditClickOpen = () => {
    setOpenEditApplication(true);
  };

  const handleCloseEdit = () => {
    setOpenEditApplication(false);
    setSelectedApp(null);
    setSelectedCheckedApps([]);
    pubSub.publish(EVENTS.ORGANIZATIONS_LIST);
  }

  const handleEditDone = () => {
    setSelectedApp(null);
    setOpenEditApplication(false);
    pubSub.publish(EVENTS.ORGANIZATIONS_LIST);
  };

  return (
    <div>
      <AppContainer>
        <StyledButton
          variant="contained"
          onClick={handleEditClickOpen}
          className="d-flex align-items-center"
          disabled={!selectedApp || isAppListLoading}
          startIcon={<EditIcon />}
        >
          {t("edit_application")}
        </StyledButton>
      </AppContainer>
      <EditApplicationDialog
        open={openEditApplication} 
        selectedApp={selectedApp}
        handleEditApplicationClose={handleCloseEdit}
        handleEditApplicationDone={handleEditDone}
      />
    </div>
  );
};

type IEditApplicationProps = {
  open: boolean;
  selectedApp: string | null;
  handleEditApplicationClose: () => void;
  handleEditApplicationDone: () => void;
};

export const EditApplicationDialog = (props: IEditApplicationProps) => {
  const { t } = useTranslation();
  const { selectedApp, setOriginalAppName } = useContext(ApplicationsContext);
  const { appService } = useService(); // Get appService
  const [loading, setLoading] = useState(true); // Add loading state
  const { config } = useContext(AppConfig);

  const defaultModel: IAddEditProviderModel = {
    0: {
      id: "",
      name: "",
      clientId: "",
      accessKey: ""
    },
    isValid: false,
    isSameValue: true
  };

  const [model, setModel] = useState<IAddEditProviderModel>(defaultModel);

  const handleClose = useCallback(() => {
    props.handleEditApplicationClose();
    setModel(defaultModel);
  }, [props.handleEditApplicationClose]);

  const handleModelChange = (updatedModel: any) => {
    const updated = updatedModel;
    setModel((prevModel) => ({
      ...prevModel,
      ...updated,
    }));
  };

  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates after component unmounts
     
    const fetchAndSetOrgData = async () => {
      if (!selectedApp || !appService.getApp) return; // Handle undefined values gracefully

      setLoading(true);

      try {
        const app = await appService.getApp(config.tenantId, selectedApp); // Fetch app data

        if (isMounted && app) { // Only update state if component is still mounted
          const updatedModel = {
              0: {
                id: app.uniqueId,
                name: app.applicationName,
                clientId: app.clientID,
                accessKey: app.subscriptionKey
              },
              isValid: false,
              isSameValue: true
          };
          setModel(updatedModel);
          setOriginalAppName(app.applicationName);
        }
      } finally { 
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (props.open) {
    fetchAndSetOrgData();
    }

    return () => {
      isMounted = false; // Set flag to false on unmount
      setOriginalAppName("");
    };
}, [selectedApp, appService, props.open]);

  return (
    <Dialog
      open={props.open}
      sx={{
        '& .MuiDialog-paper': {
          width: '700px',
          maxWidth: 'none'
        },
      }}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      disableEscapeKeyDown
      aria-labelledby="form-dialog-title"
    >
      <StyledDialogTitle id="form-dialog-title">
        <StyledTypography>{t("edit_application")}</StyledTypography>
      </StyledDialogTitle>
      <StyledDialogContent>
        <EditCustomerComponent
            handleDone={() => {
              handleClose();
              props.handleEditApplicationDone();
            }}
            handleClose={handleClose}
            model={model}
            handleModelChange={handleModelChange}
        />
      </StyledDialogContent>
    </Dialog>
  );
};

export default EditApplicationComponent;
